import shortid from 'shortid'
import { getNotes, setNotes } from '@/utils/services'
import { trackEvent, identifyUser } from '@/utils/analytics'
import debounce from 'lodash/debounce'
import isElectron from '@/utils/is-electron'
import { encryptNotes, decryptNotes } from '@/utils/crypto'

export default {
  async initialize({ commit, dispatch, getters }, user) {
    commit('setInitialized', false)
    commit('setUser', user)
    if (user) identifyUser(user.email)
    await dispatch('encryption/getKeys')
    await dispatch('getNotes')
    if (!getters['encryption/promptForPassphrase']) {
      commit('setInitialized', true)
      trackEvent('initialize app', {
        eventCategory: 'app',
        eventAction: 'initialize'
      })
    }
  },
  updateNote({ commit, dispatch, state }, note) {
    note.modified = Date.now()
    const cleanNote = ({ title, content, created, isRoot }) => ({
      title, content, isRoot: Boolean(isRoot), created, modified: Date.now()
    })
    commit('updateNote', { noteId: note.id, note: cleanNote(note) })
    trackEvent('update note', {
      eventCategory: 'note',
      eventAction: 'update'
    })
    dispatch('syncNotes', state.notes)
  },
  deleteNote({ commit, dispatch, state }, note) {
    commit('deleteNote', note)
    trackEvent('delete note', {
      eventCategory: 'note',
      eventAction: 'delete'
    })
    dispatch('syncNotes', state.notes)
  },
  addNote({ commit, dispatch, state }, note) {
    note.id = shortid.generate()
    note.created = Date.now()
    note.modified = Date.now()
    commit('addNote', note)
    dispatch('syncNotes', state.notes)
    return note
  },
  setRootNote({ commit, getters, dispatch, state }, { id }) {
    commit('setRoot', { id, isRoot: true })
    getters.notes.filter(n => n.id !== id).map(n => commit('setRoot', { id: n.id, isRoot: false }))
    dispatch('syncNotes', state.notes)
  },
  async getNotes(context) {
    const userUid = context.state.user ? context.state.user.uid : null
    if (userUid) {
      const notes = await getNotes(userUid)
      const encryptionKey = context.state.encryption.encryptionKey
      if (Object.keys(notes).length > 0) {
        let notesToCommit
        if (encryptionKey) {
          try {
            notesToCommit = decryptNotes(notes, encryptionKey)
          } catch (e) {
            console.log(e)
            notesToCommit = notes
          }
        } else {
          notesToCommit = notes
        }
        context.commit('setNotes', notesToCommit)
      } else {
        await context.dispatch('setDefaultNotes')
      }
    } else {
      try {
        const localNotes = JSON.parse(localStorage.getItem('notes'))
        if (Object.keys(localNotes).length > 0) {
          context.commit('setNotes', localNotes)
        } else {
          await context.dispatch('setDefaultNotes')
        }
      } catch (e) {
        await context.dispatch('setDefaultNotes')
      }
    }
    // Make sure there is a root note
    const { rootNote, notes } = context.getters
    if (!rootNote) {
      const candidateRootNote = notes.sort((a, b) => (b.links.to.length + b.links.from.length) - (a.links.to.length + a.links.from.length))[0]
      if (candidateRootNote) context.dispatch('setRootNote', candidateRootNote)
    }
  },
  syncNotes: debounce(function ({ state, getters }, notes) {
    if (isElectron()) {
      // eslint-disable-next-line
      const { ipcRenderer } = __non_webpack_require__('electron') || {}
      ipcRenderer.send('syncNotes', notes)
    }
    const user = state.user || {}
    if (user && user.emailVerified) {
      const { encryptionKey } = state.encryption
      if (encryptionKey) {
        const encryptedNotes = encryptNotes(notes, encryptionKey)
        setNotes(user.uid, encryptedNotes)
      } else {
        setNotes(user.uid, notes)
      }
    } else {
      localStorage.setItem('notes', JSON.stringify(notes))
    }
  }, 500),
  async setDefaultNotes({ dispatch, getters, commit }) {
    commit('clearNotes')
    const initialNotes = await Promise.all([{
      isRoot: true,
      title: 'Your first note',
      content: `Contexted is a **relational note-taking app**. Use it as your personal knowledge base, research assistent or just to map out thoughts.\n\n
### **How does it work?**
*   Create a new note by typing words between [[brackets]]
*   Click on **Mindmap mode** in the menu left to visualize your notes
\n
Let's get started!`
    },
    {
      title: 'brackets',
      content: `If you type square brackets around text a link is created automatically. Like magic!`
    }
    ].map(async note => dispatch('addNote', note)))
    return initialNotes
  },
  sendVerificationEmail({ state }) {
    state.user.sendEmailVerification()
  },
  setViewMode({ commit }, viewMode) {
    commit('setViewMode', viewMode)
  },
  setSideBarCollapsed({ commit }, sideBarCollapsed) {
    commit('setSideBarCollapsed', sideBarCollapsed)
  }
}
